import { EventType } from '@graphql/generated-base';

import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventFeatureFlags } from './useEventFeatureFlags/useEventFeatureFlags';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

export enum EventFeature {
  // use the new event admin invite workflow that has no legacy dependencies
  adminInvite,
  // enable access to carbon integration
  // This will also hide wedding specific features for event type wedding
  carbonEnabled,
  //enables cash funds messaging type
  cashFundMessaging,
  // Disables showing the website page Add App
  disableAddAppPage,
  // disable UX that exposes links to legacy RSVP route and behavior
  disableLegacyRsvp,
  // disable settings and features that are reliant on legacy system
  disableLegacyWebFeatures,
  // Disables RSVP smart tips in Questions
  disableRsvpSmartTips,
  // Disables wedding specific partner 1 and partner 2 name inputs in settings
  disableSettingPersonalInfo,
  // Disables the floating action bar identifying a user as a guest and asking them to RSVP via legacy RSVP
  disableWebActionBar,
  // disable the website theme popover to hide wedding specific content
  disableWebThemePopover,
  // Enable custom link option in general messaging (general and carbon only, not weddings)
  generalMessageCustomLink,
  // disables the overview onboarding carousel in the website overview page
  hideWebOverviewCarousel,
  // use pro icon navigation in hamburger
  menu,
  // Use general/carbon event message tracking logic (distinguishes from wedding events)
  messageTracking,
  // use pro messaging with joy pro email templates (for general events and weddings)
  messaging,
  // use the new pro admin dashboard
  proDashboard,
  // enable registry in settings and navigation and UX cross sell
  registry,
  // Wedding websites have special logic around the about page always being first
  // General events are all custom pages and can have any page first
  reorderAllWebsitePages,
  // General events do not use firebase or legacy API.  Attempting to sync contacts with firebase will throw errors
  skipContactFirebaseSync,
  // enable stationery in navigation and UX cross sell
  stationery,
  // enable ticketing dashboard for Carbon events
  tickets,
  // Enable carbon scoped settings(carbon only)
  useCarbonScopedSettings,
  // Enable carbon scoped website editor (overview, photos, pages, etc..) (carbon only)
  useCarbonScopedWebsiteEditor,
  // Disconnects a carbon created event from the carbon workspace and uses the pro admin dashboard instead
  useCarbonWorkspace,
  //people picker from weddings, support tags
  useLegacyPeoplePicker,
  // Enable cash fund component in website editor (weddings only, requires newCashFundsEnabled + websiteCashFundComponentEnabled)
  websiteCashFundComponent,
  // Expose the Pro version of website custom components
  websiteComponents,
  // Use general/carbon naming for website features (distinguishes from wedding events)
  websiteFeatureNaming,
  // Use pro website header config to avoid wedding content
  websiteHeaderConfig,
  // Enables setting the website preview photo from general settings
  websitePhotoSetting,
  // Use the pro version of the website theme gallery which removes wedding designs and imagery
  websiteThemes,
}

/** @internal Only exported for unit testing. */
export type FeatureEvaluatorArgs = {
  readonly isGeneral: boolean;
  readonly isWedding: boolean;
  readonly isCarbonEnabled: boolean;
  readonly isModernNav: boolean;
  readonly disableCarbonWorkspaceRouting: boolean;
  readonly isWeddingGeneralMessagingEnabled: boolean;
  readonly isNewCashFundsEnabled: boolean;
  readonly isWebsiteCashFundComponentEnabled: boolean;
  readonly isCarbonTicketingEnabled: boolean;
};

const anyOptionTrue = ({ isGeneral, isCarbonEnabled, isModernNav }: FeatureEvaluatorArgs) => isGeneral || isCarbonEnabled || isModernNav;

/** @internal Only exported for unit testing. */
export const featureEvaluators: Readonly<Record<EventFeature, (args: FeatureEvaluatorArgs) => boolean>> = {
  [EventFeature.messaging]: ({ isGeneral, isWedding, isCarbonEnabled, isWeddingGeneralMessagingEnabled }) => {
    if (isGeneral || isCarbonEnabled) {
      return true;
    }
    if (isWedding) {
      return isWeddingGeneralMessagingEnabled;
    }
    return false;
  },
  [EventFeature.adminInvite]: args => anyOptionTrue(args),
  [EventFeature.carbonEnabled]: ({ isCarbonEnabled }) => isCarbonEnabled,
  [EventFeature.cashFundMessaging]: ({ isWedding, isWeddingGeneralMessagingEnabled }) => isWedding && isWeddingGeneralMessagingEnabled,
  [EventFeature.disableWebActionBar]: ({ isGeneral }) => isGeneral,
  [EventFeature.disableLegacyRsvp]: ({ isGeneral }) => isGeneral,
  [EventFeature.disableWebThemePopover]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.disableLegacyWebFeatures]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.disableSettingPersonalInfo]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.disableAddAppPage]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.disableRsvpSmartTips]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.generalMessageCustomLink]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.hideWebOverviewCarousel]: args => anyOptionTrue(args),
  [EventFeature.menu]: args => anyOptionTrue(args),
  [EventFeature.messageTracking]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.proDashboard]: args => anyOptionTrue(args),
  [EventFeature.skipContactFirebaseSync]: ({ isGeneral }) => isGeneral,
  [EventFeature.stationery]: ({ isGeneral, isCarbonEnabled }) => !isGeneral && !isCarbonEnabled,
  [EventFeature.tickets]: ({ isCarbonEnabled, isCarbonTicketingEnabled, isGeneral }) => (isCarbonEnabled || isGeneral) && isCarbonTicketingEnabled,
  [EventFeature.registry]: ({ isGeneral, isCarbonEnabled }) => !isGeneral && !isCarbonEnabled,
  [EventFeature.reorderAllWebsitePages]: ({ isGeneral }) => isGeneral,
  [EventFeature.useCarbonScopedSettings]: ({ isCarbonEnabled }) => isCarbonEnabled,
  [EventFeature.useCarbonScopedWebsiteEditor]: ({ isCarbonEnabled }) => isCarbonEnabled,
  [EventFeature.useCarbonWorkspace]: ({ disableCarbonWorkspaceRouting }) => !disableCarbonWorkspaceRouting,
  [EventFeature.useLegacyPeoplePicker]: ({ isWedding, isWeddingGeneralMessagingEnabled }) => isWedding && isWeddingGeneralMessagingEnabled,
  [EventFeature.websiteFeatureNaming]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.websiteCashFundComponent]: ({ isGeneral, isCarbonEnabled, isNewCashFundsEnabled, isWebsiteCashFundComponentEnabled }) =>
    !isGeneral && !isCarbonEnabled && isNewCashFundsEnabled && isWebsiteCashFundComponentEnabled,
  [EventFeature.websiteComponents]: args => anyOptionTrue(args),
  [EventFeature.websiteHeaderConfig]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.websitePhotoSetting]: args => anyOptionTrue(args),
  [EventFeature.websiteThemes]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
};

const evaluateFeature = (
  feature: EventFeature,
  isGeneral: boolean,
  isWedding: boolean,
  isCarbonEnabled: boolean,
  isModernNav: boolean,
  disableCarbonWorkspaceRouting: boolean,
  isWeddingGeneralMessagingEnabled: boolean,
  isNewCashFundsEnabled: boolean,
  isWebsiteCashFundComponentEnabled: boolean,
  isCarbonTicketingEnabled: boolean,
) => {
  const evaluator = featureEvaluators[feature];
  if (!evaluator) {
    return false;
  }

  return evaluator({
    isGeneral,
    isWedding,
    isCarbonEnabled,
    isModernNav,
    disableCarbonWorkspaceRouting,
    isWeddingGeneralMessagingEnabled,
    isNewCashFundsEnabled,
    isWebsiteCashFundComponentEnabled,
    isCarbonTicketingEnabled,
  });
};

/**
 * Determines whether the provided event feature should be enabled for the current event.
 * @param feature The feature flag to evaluate using event type and remote feature flags.
 */
export const useProFeature = (feature: EventFeature): boolean => {
  const { eventInfo, eventHandle } = useEventInfo();
  const eventType = eventInfo?.eventType;

  const { loadingEventFeatureFlags, isEventFeatureFlagEnabled } = useEventFeatureFlags({ eventHandle: eventHandle ?? '', skip: !eventHandle });
  const isCarbonEnabled = loadingEventFeatureFlags ? false : (isEventFeatureFlagEnabled?.('useCarbon') ?? false);
  const useModernNavigation = loadingEventFeatureFlags ? false : (isEventFeatureFlagEnabled?.('useModernNavigation') ?? false);
  const disableCarbonWorkspaceRouting = loadingEventFeatureFlags ? false : (isEventFeatureFlagEnabled?.('disableCarbonWorkspaceRouting') ?? false);
  const isGeneral = eventType === EventType.general;
  const isWedding = eventType === EventType.wedding;

  const [isWeddingGeneralMessagingEnabled] = useIsFeatureEnabled('weddingGeneralMessagingEnabled');

  const [isNewCashFundsEnabled] = useIsFeatureEnabled('newCashFundsEnabled');
  const [isWebsiteCashFundComponentEnabled] = useIsFeatureEnabled('websiteCashFundComponentEnabled');
  const [isCarbonTicketingEnabled] = useIsFeatureEnabled('carbonTicketingEnabled');

  return evaluateFeature(
    feature,
    isGeneral,
    isWedding,
    isCarbonEnabled,
    useModernNavigation,
    disableCarbonWorkspaceRouting,
    isWeddingGeneralMessagingEnabled,
    isNewCashFundsEnabled,
    isWebsiteCashFundComponentEnabled,
    isCarbonTicketingEnabled,
  );
};
